import React, { useCallback, useState, createContext, useContext } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
type ToastType = 'success' | 'error' | 'info' | 'warning';
interface Toast {
  id: string;
  type: ToastType;
  message: string;
}
interface ToastContextType {
  addToast: (message: string, type?: ToastType) => void;
}
const ToastContext = createContext<ToastContextType | undefined>(undefined);
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
export function ToastProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const addToast = useCallback((message: string, type: ToastType = 'success') => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts(prev => [...prev, {
      id,
      type,
      message
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }, []);
  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };
  return <ToastContext.Provider value={{
    addToast
  }} data-id="element-176">
      {children}
      <div className="fixed top-4 right-4 z-[60] flex flex-col gap-2 w-full max-w-sm pointer-events-none" data-id="element-177">
        {toasts.map(toast => <div key={toast.id} className={`
              pointer-events-auto flex items-center gap-3 p-4 rounded-lg shadow-lg border animate-in slide-in-from-right duration-300
              ${toast.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : ''}
              ${toast.type === 'error' ? 'bg-red-500/10 border-red-500/20 text-red-400' : ''}
              ${toast.type === 'warning' ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' : ''}
              ${toast.type === 'info' ? 'bg-blue-500/10 border-blue-500/20 text-blue-400' : ''}
              bg-slate-900
            `} data-id="element-178">
            {toast.type === 'success' && <CheckCircle className="h-5 w-5 flex-shrink-0" data-id="element-179" />}
            {toast.type === 'error' && <AlertCircle className="h-5 w-5 flex-shrink-0" data-id="element-180" />}
            {toast.type === 'warning' && <AlertTriangle className="h-5 w-5 flex-shrink-0" data-id="element-181" />}
            {toast.type === 'info' && <Info className="h-5 w-5 flex-shrink-0" data-id="element-182" />}

            <p className="text-sm font-medium flex-1" data-id="element-183">{toast.message}</p>

            <button onClick={() => removeToast(toast.id)} className="text-slate-400 hover:text-white transition-colors" data-id="element-184">
              <X className="h-4 w-4" data-id="element-185" />
            </button>
          </div>)}
      </div>
    </ToastContext.Provider>;
}